export type EventRegistrationAction =
  /** The event is over. No registration path applies to anyone. */
  | "has_ended"
  | "sign_in"
  | "register"
  | "external_checkout"
  | "external_registration"
  | "join"
  /** A special-event registration awaiting admin review. Never joinable. */
  | "pending_review"
  | "unavailable";

export const PENDING_REVIEW_MESSAGE = "Registration received / under review";

export type PublicEventPricingPolicy = {
  pricingMode?: "free" | "paid_external" | "paid_internal";
  checkoutAvailability?: "available" | "not_configured" | "unavailable";
  externalCheckoutUrl?: string | null;
  /**
   * CHI-20 (Dev2 0034): top level, always present. With no pricing row the server returns
   * ONLY this field, so every pricing field above may be missing: read them with ?. and never
   * treat a non-null policy as "has pricing". Read the flag via eventCta.ticketsCheckoutEnabled.
   */
  ticketsCheckoutEnabled?: boolean;
} | null | undefined;

/**
 * Resolves the next public event action before either event surface performs a
 * side effect. Authentication is evaluated ahead of every acting branch, so no
 * browser path can open a registration, checkout, or meeting route for a
 * signed-out visitor. Only `has_ended` sits above it, and it acts on nothing.
 */
export function resolveEventRegistrationAction(input: {
  isAuthenticated: boolean;
  pricingPolicy?: PublicEventPricingPolicy;
  externalRegistrationUrl?: string;
  joinUrl?: string;
  /** Whether the event's date has already passed. */
  hasEnded?: boolean;
  /** The signed-in account's durable registration state for this event, if any. */
  registrationState?: string;
  /**
   * Approval-queue (special) event. Its CTA always goes to the server's
   * register step (pending review), never to an external ticket link or
   * checkout; an approved (registered) attendee goes straight to join.
   */
  isSpecialEvent?: boolean;
}): EventRegistrationAction {
  /*
   * Checked before authentication, because a finished event is finished for
   * everyone. Evaluating sign-in first offered a signed-out visitor "Sign In to
   * Register" for a talk that happened last month, and a signed-in one "Request
   * to Attend" — an invitation the site cannot honour.
   */
  if (input.hasEnded) return "has_ended";

  if (!input.isAuthenticated) return "sign_in";

  // Ahead of every branch below, including `join`: a held registration has no meeting access.
  if (input.registrationState === "pending_review") return "pending_review";

  // G4.1: ahead of every pricing and external branch, for every registration state.
  if (input.isSpecialEvent === true) {
    if (input.registrationState === "registered" && input.joinUrl) return "join";
    return "register";
  }

  const policy = input.pricingPolicy;
  if (policy?.pricingMode === "paid_internal") {
    return policy.checkoutAvailability === "available" ? "register" : "unavailable";
  }
  if (
    policy?.checkoutAvailability === "not_configured" ||
    policy?.checkoutAvailability === "unavailable"
  ) {
    return "unavailable";
  }

  if (policy?.pricingMode === "paid_external") {
    return policy.externalCheckoutUrl ? "external_checkout" : "unavailable";
  }

  if (input.externalRegistrationUrl) return "external_registration";
  if (input.joinUrl) return "join";
  return "register";
}

/*
 * CHI-20: an internally Stripe-paid event buys tickets on its own page instead
 * of registering in place. Generic for every such event (no slug rules).
 * Special events win (G4.1): a special event never routes here, paid or not.
 */
export function shouldUseTicketsPage(input: { pricingPolicy?: PublicEventPricingPolicy; isSpecialEvent?: boolean }) {
  return (
    input.isSpecialEvent !== true &&
    input.pricingPolicy?.pricingMode === "paid_internal" &&
    input.pricingPolicy.checkoutAvailability === "available"
  );
}

export function getEventTicketsUrl(slug: string) {
  return `/events/${encodeURIComponent(slug)}/tickets`;
}
